import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../components/AdminLayout';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  useTheme,
} from '@mui/material';
import { Add, Edit, Delete, LocationOn, Phone, Email, Schedule, EventBusy } from '@mui/icons-material';
import BranchScheduleDialog from './BranchScheduleDialog';
import {
  getBusinessDayBounds,
  parseOrderDate,
  parseOrderTotal,
  isRevenueOrder,
} from '../../utils/reportingUtils';

interface Branch {
  id: string | number;
  name: string;
  address: string;
  phone: string;
  email: string;
  manager: string;
  status: 'active' | 'inactive';
  orders_today: number;
  revenue_today: number;
  opening_hours: string;
}

const BranchesPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingBranch, setEditingBranch] = useState<Branch | null>(null);
  const [scheduleBranch, setScheduleBranch] = useState<Branch | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    email: '',
    manager: '',
    opening_hours: '',
  });

  // Fetch branches data from Firebase and calculate real statistics
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('🏢 [BRANCHES] Fetching branches and calculating real statistics...');
        
        // First, check if user is authenticated
        const { onAuthStateChanged } = await import('firebase/auth');
        const { collection, getDocs, query, where } = await import('../../services/firestoreCompat');
        const { db, auth } = await import('../../services/firebaseConfig');
        
        // Wait for auth state to be ready
        const currentUser = auth.currentUser;
        if (!currentUser) {
          console.warn('⚠️ [BRANCHES] No authenticated user found. Waiting for auth state...');
          
          // Wait for auth state change
          return new Promise<void>((resolve) => {
            const unsubscribe = onAuthStateChanged(auth, async (user) => {
              unsubscribe();
              if (user) {
                console.log('✅ [BRANCHES] User authenticated:', user.email);
                // Retry fetching after auth is ready
                setTimeout(() => fetchBranches(), 100);
              } else {
                console.error('❌ [BRANCHES] User not authenticated');
                setError('Please log in to access branches. Redirecting to login...');
                setLoading(false);
                setTimeout(() => {
                  window.location.href = '/login';
                }, 2000);
              }
              resolve();
            });
          });
        }
        
        console.log('✅ [BRANCHES] User authenticated:', currentUser.email);
        
        // Fetch branches from Firebase
        const branchesRef = collection(db, 'branches');
        const branchesSnapshot = await getDocs(branchesRef);
        
        let branchesList: Branch[] = [];
        
        if (branchesSnapshot.empty) {
          // If no branches exist, create the Saheeh branch
          console.log('⚠️ [BRANCHES] No branches found, creating Saheeh branch...');
          try {
            const { collection, addDoc, serverTimestamp } = await import('../../services/firestoreCompat');
            const branchData = {
              name: 'Saheeh',
              address: 'Sudetenstraße 60, 50354 Hürth',
              address_line1: 'Sudetenstraße 60',
              city: 'Hürth',
              postal_code: '50354',
              phone: '022337063324',
              email: 'hurth@saheehpos.com',
              manager: 'Admin',
              opening_hours: '10:00 - 22:00',
              is_active: true,
              is_accepting_orders: true,
              created_at: serverTimestamp(),
              updated_at: serverTimestamp()
            };
            
            const docRef = await addDoc(collection(db, 'branches'), branchData);
            console.log('✅ [BRANCHES] Created Saheeh branch:', docRef.id);
            
            branchesList = [{
              id: docRef.id,
              name: 'Saheeh',
              address: 'Sudetenstraße 60, 50354 Hürth',
              phone: '022337063324',
              email: 'hurth@saheehpos.com',
              manager: 'Admin',
            status: 'active',
            orders_today: 0,
            revenue_today: 0,
              opening_hours: '10:00 - 22:00'
            }];
          } catch (createError) {
            console.error('❌ [BRANCHES] Failed to create default branch:', createError);
            throw createError;
          }
        } else {
          // Process branches from Firebase
          branchesList = branchesSnapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              name: data.name || 'Unnamed Branch',
              address: data.address || data.address_line1 || 'Address not set',
              phone: data.phone || '',
              email: data.email || '',
              manager: data.manager || 'Not assigned',
              status: (data.is_active !== false && data.is_accepting_orders !== false) ? 'active' : 'inactive',
              orders_today: 0, // Will be calculated below
              revenue_today: 0, // Will be calculated below
              opening_hours: data.opening_hours || data.operating_hours || '10:00 - 22:00'
            };
          });
          
          // Remove duplicates - keep only the first occurrence of each unique branch (by name and address)
          const uniqueBranches = new Map<string, Branch>();
          const duplicatesToDelete: string[] = [];
          
          branchesList.forEach(branch => {
            const key = `${branch.name.toLowerCase().trim()}_${branch.address.toLowerCase().trim()}`;
            if (!uniqueBranches.has(key)) {
              uniqueBranches.set(key, branch);
            } else {
              // Mark duplicate for deletion
              console.log('⚠️ [BRANCHES] Found duplicate branch:', branch.id, branch.name);
              duplicatesToDelete.push(branch.id as string);
            }
          });
          
          // Delete duplicates from Firebase
          if (duplicatesToDelete.length > 0) {
            console.log(`⚠️ [BRANCHES] Deleting ${duplicatesToDelete.length} duplicate branch(es)...`);
            const { doc, deleteDoc } = await import('../../services/firestoreCompat');
            
            for (const duplicateId of duplicatesToDelete) {
              try {
                await deleteDoc(doc(db, 'branches', duplicateId));
                console.log(`✅ [BRANCHES] Deleted duplicate branch: ${duplicateId}`);
              } catch (deleteError) {
                console.error('❌ [BRANCHES] Failed to delete duplicate branch:', deleteError);
              }
            }
          }
          
          branchesList = Array.from(uniqueBranches.values());
          
          // Additional check: If there are multiple "Saheeh" branches, keep only the first one
          const saheehBranches = branchesList.filter(b => 
            (b.name.toLowerCase().includes('hürth') || b.name.toLowerCase().includes('hurth')) &&
            b.address.toLowerCase().includes('sudetenstraße')
          );
          
          if (saheehBranches.length > 1) {
            console.log(`⚠️ [BRANCHES] Found ${saheehBranches.length} duplicate Saheeh branches, keeping first and deleting others...`);
            const { doc, deleteDoc } = await import('../../services/firestoreCompat');
            
            // Keep the first one (oldest or first in list), delete the rest
            for (let i = 1; i < saheehBranches.length; i++) {
              const duplicateBranch = saheehBranches[i];
              try {
                await deleteDoc(doc(db, 'branches', duplicateBranch.id as string));
                console.log(`✅ [BRANCHES] Deleted duplicate Saheeh branch: ${duplicateBranch.id}`);
                // Remove from branchesList
                branchesList = branchesList.filter(b => b.id !== duplicateBranch.id);
              } catch (deleteError) {
                console.error('❌ [BRANCHES] Failed to delete duplicate branch:', deleteError);
              }
            }
          }
          
          // If there's only one branch and it's not the correct one, update it
          if (branchesList.length === 1 && branchesList[0].name !== 'Saheeh') {
            console.log('⚠️ [BRANCHES] Updating existing branch to Saheeh...');
            try {
              const { doc, updateDoc } = await import('../../services/firestoreCompat');
              const branchRef = doc(db, 'branches', branchesList[0].id as string);
              await updateDoc(branchRef, {
                name: 'Saheeh',
                address: 'Sudetenstraße 60, 50354 Hürth',
                address_line1: 'Sudetenstraße 60',
                city: 'Hürth',
                postal_code: '50354',
                phone: '022337063324',
                email: 'hurth@saheehpos.com',
                manager: 'Admin',
                opening_hours: '10:00 - 22:00',
                is_active: true,
                is_accepting_orders: true
              });
              
              // Update the local list
              branchesList[0] = {
                ...branchesList[0],
                name: 'Saheeh',
                address: 'Sudetenstraße 60, 50354 Hürth',
                phone: '022337063324',
                email: 'hurth@saheehpos.com',
                manager: 'Admin',
                opening_hours: '10:00 - 22:00'
              };
              console.log('✅ [BRANCHES] Branch updated to Saheeh');
            } catch (updateError) {
              console.error('❌ [BRANCHES] Failed to update branch:', updateError);
            }
          }
        }
        
        // Fetch today's orders to calculate statistics
        // Uses shared business-day boundary (06:00 local) and shared revenue
        // filter so Branches reconciles with POS Orders + Admin Reports.
        const { start: todayStart, end: todayEnd } = getBusinessDayBounds('today');

        const ordersRef = collection(db, 'orders');
        const ordersSnapshot = await getDocs(ordersRef);

        // Calculate orders and revenue per branch for today
        const branchStats = new Map<string, { orders: number; revenue: number }>();

        ordersSnapshot.forEach((doc) => {
          const orderData = doc.data();
          const orderDate = parseOrderDate(orderData);
          if (!orderDate) return;
          if (orderDate < todayStart) return;
          if (todayEnd && orderDate >= todayEnd) return;
          if (!isRevenueOrder(orderData)) return;

          const branch = orderData.branch || 'saheeh-riyadh';
          const orderTotal = parseOrderTotal(orderData);

          if (!branchStats.has(branch)) {
            branchStats.set(branch, { orders: 0, revenue: 0 });
          }

          const stats = branchStats.get(branch)!;
          stats.orders += 1;
          stats.revenue += orderTotal;
        });
        
        // Update branches with real statistics
        branchesList = branchesList.map(branch => {
          // Match branch by name - prioritize Hürth
          const branchKey = branch.name.toLowerCase().includes('hürth') || branch.name.toLowerCase().includes('hurth')
            ? 'saheeh-riyadh'
            : branch.name.toLowerCase().includes('berlin') || branch.name.toLowerCase().includes('mitte') 
            ? 'saheeh-riyadh' 
            : branch.name.toLowerCase().replace(/\s+/g, '-').toLowerCase();
          
          const stats = branchStats.get('saheeh-riyadh') || branchStats.get('saheeh-riyadh') || branchStats.get(branchKey) || { orders: 0, revenue: 0 };
          
          return {
            ...branch,
            orders_today: stats.orders,
            revenue_today: parseFloat(stats.revenue.toFixed(2))
          };
        });
        
        console.log('✅ [BRANCHES] Branches loaded with real statistics:', branchesList);
        setBranches(branchesList);
      } catch (error: any) {
        console.error('❌ [BRANCHES] Failed to fetch branches:', error);
        const errorMessage = error?.message || 'Unknown error';
        const errorCode = error?.code || '';
        
        // Check if it's an authentication/permission error
        if (errorCode === 'permission-denied' || errorMessage.includes('permission') || errorMessage.includes('insufficient')) {
          const { auth } = await import('../../services/firebaseConfig');
          if (!auth.currentUser) {
            setError('Please log in to access branches. Redirecting to login...');
            setTimeout(() => {
              window.location.href = '/login';
            }, 2000);
          } else {
            console.error('❌ [BRANCHES] Permission denied for authenticated user:', auth.currentUser.email);
            setError(`Permission denied. User: ${auth.currentUser.email}. Please check Firestore rules. Error: ${errorMessage}`);
          }
        } else {
          setError(`Failed to load branches: ${errorMessage}. Please check Firebase connection.`);
        }
        
        // Fallback to Saheeh branch info (but don't save to Firebase if error occurred)
        setBranches([{
          id: 'default',
          name: 'Saheeh',
          address: 'Sudetenstraße 60, 50354 Hürth',
          phone: '022337063324',
          email: 'hurth@saheehpos.com',
          manager: 'Admin',
          status: 'active',
          orders_today: 0,
          revenue_today: 0,
          opening_hours: '10:00 - 22:00'
        }]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchBranches();
    
    // Set up real-time listener for orders to update statistics
    const setupRealtimeListener = async () => {
      try {
        const { collection, onSnapshot } = await import('../../services/firestoreCompat');
        const { db } = await import('../../services/firebaseConfig');
        
        const ordersRef = collection(db, 'orders');
        const unsubscribe = onSnapshot(ordersRef, () => {
          // Refresh branch statistics when orders change
          fetchBranches();
        });
        
        return unsubscribe;
      } catch (error) {
        console.error('Failed to set up real-time listener:', error);
      }
    };
    
    const unsubscribePromise = setupRealtimeListener();
    
    return () => {
      unsubscribePromise.then(unsubscribe => {
        if (unsubscribe) unsubscribe();
      });
    };
  }, []);

  const handleOpenDialog = (branch?: Branch) => {
    if (branch) {
      setEditingBranch(branch);
      setFormData({
        name: branch.name,
        address: branch.address,
        phone: branch.phone,
        email: branch.email,
        manager: branch.manager,
        opening_hours: branch.opening_hours,
      });
    } else {
      setEditingBranch(null);
      setFormData({
        name: '',
        address: '',
        phone: '',
        email: '',
        manager: '',
        opening_hours: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingBranch(null);
    setFormData({
      name: '',
      address: '',
      phone: '',
      email: '',
      manager: '',
      opening_hours: '',
    });
  };

  const handleSaveBranch = async () => {
    try {
      setLoading(true);
      const { collection, addDoc, doc, updateDoc, serverTimestamp } = await import('../../services/firestoreCompat');
      const { db } = await import('../../services/firebaseConfig');
      
      if (editingBranch && editingBranch.id !== 'default') {
        // Update existing branch in Firebase
        const branchRef = doc(db, 'branches', editingBranch.id as string);
        await updateDoc(branchRef, {
          name: formData.name,
          address: formData.address,
          address_line1: formData.address,
          phone: formData.phone,
          email: formData.email,
          manager: formData.manager,
          opening_hours: formData.opening_hours,
          updated_at: serverTimestamp()
        });
        console.log('✅ [BRANCHES] Branch updated in Firebase');
    } else {
        // Create new branch in Firebase
        const branchData = {
          name: formData.name,
          address: formData.address,
          address_line1: formData.address,
          phone: formData.phone,
          email: formData.email,
          manager: formData.manager,
          opening_hours: formData.opening_hours,
          is_active: true,
          is_accepting_orders: true,
          created_at: serverTimestamp(),
          updated_at: serverTimestamp()
        };
        
        const docRef = await addDoc(collection(db, 'branches'), branchData);
        console.log('✅ [BRANCHES] Branch created in Firebase:', docRef.id);
      }
      
      // Refresh branches list
      const fetchBranches = async () => {
        try {
          const { collection, getDocs } = await import('../../services/firestoreCompat');
          const { db } = await import('../../services/firebaseConfig');
          
          const branchesRef = collection(db, 'branches');
          const branchesSnapshot = await getDocs(branchesRef);
          
          let branchesList: Branch[] = [];
          
          if (!branchesSnapshot.empty) {
            branchesList = branchesSnapshot.docs.map(doc => ({
              id: doc.id,
              name: doc.data().name || 'Unnamed Branch',
              address: doc.data().address || doc.data().address_line1 || 'Address not set',
              phone: doc.data().phone || '',
              email: doc.data().email || '',
              manager: doc.data().manager || 'Not assigned',
              status: (doc.data().is_active !== false && doc.data().is_accepting_orders !== false) ? 'active' : 'inactive',
        orders_today: 0,
        revenue_today: 0,
              opening_hours: doc.data().opening_hours || doc.data().operating_hours || '10:00 - 22:00'
            }));
          }
          
          // Calculate statistics
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          const ordersRef = collection(db, 'orders');
          const ordersSnapshot = await getDocs(ordersRef);
          
          const branchStats = new Map<string, { orders: number; revenue: number }>();
          
          ordersSnapshot.forEach((doc) => {
            const orderData = doc.data();
            const orderDate = orderData.created_at?.toDate?.() || 
                            (orderData.created_at?.seconds ? new Date(orderData.created_at.seconds * 1000) : 
                             (typeof orderData.created_at === 'string' ? new Date(orderData.created_at) : null));
            
            if (orderDate && orderDate >= today) {
              const branch = orderData.branch || 'saheeh-riyadh';
              const orderTotal = parseFloat(orderData.total || 0);
              
              if (!branchStats.has(branch)) {
                branchStats.set(branch, { orders: 0, revenue: 0 });
              }
              
              const stats = branchStats.get(branch)!;
              stats.orders += 1;
              stats.revenue += orderTotal;
            }
          });
          
          branchesList = branchesList.map(branch => {
            const branchKey = branch.name.toLowerCase().includes('hürth') 
              ? 'saheeh-riyadh'
              : branch.name.toLowerCase().replace(/\s+/g, '-').toLowerCase();
            
            const stats = branchStats.get('saheeh-riyadh') || branchStats.get(branchKey) || { orders: 0, revenue: 0 };
            
            return {
              ...branch,
              orders_today: stats.orders,
              revenue_today: parseFloat(stats.revenue.toFixed(2))
            };
          });
          
          setBranches(branchesList);
        } catch (error) {
          console.error('Failed to refresh branches:', error);
        }
      };
      
      await fetchBranches();
      handleCloseDialog();
    } catch (error) {
      console.error('❌ [BRANCHES] Failed to save branch:', error);
      setError('Failed to save branch. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBranch = async (id: string | number) => {
    if (id === 'default') {
      return; // Don't delete default branch
    }
    
    try {
      setLoading(true);
      const { doc, deleteDoc } = await import('../../services/firestoreCompat');
      const { db } = await import('../../services/firebaseConfig');
      
      await deleteDoc(doc(db, 'branches', id as string));
      console.log('✅ [BRANCHES] Branch deleted from Firebase');
      
      // Refresh branches list by calling fetchBranches
      window.location.reload(); // Simple refresh for now
    } catch (error) {
      console.error('❌ [BRANCHES] Failed to delete branch:', error);
      setError('Failed to delete branch. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return `€${amount.toFixed(2)}`;
  };

  return (
    <AdminLayout>
      <Box sx={{ p: { xs: 1.5, md: 4 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
          <Typography 
            variant="h3" 
            sx={{ 
              color: '#e0e0e0', 
              fontWeight: 700,
              fontFamily: '"Poppins", sans-serif'
            }}
          >
            {t('branches.title')}
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
            sx={{ 
              backgroundColor: '#00C896', 
              color: '#1a1d2e',
              '&:hover': { 
                backgroundColor: '#7bc912' 
              },
              fontFamily: '"Poppins", sans-serif',
              fontWeight: 600
            }}
          >
            {t('branches.addBranch')}
          </Button>
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: '#e0e0e0' }} />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && (
          <>
            {/* Branch Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={4}>
                <Card sx={{ 
                  backgroundColor: '#252838',
                  border: '1px solid #3a3d52',
                  borderRadius: 3,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <LocationOn sx={{ fontSize: 40, mr: 2, color: '#4CAF50' }} />
                      <Box>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: '#9ca3af',
                            fontFamily: '"Poppins", sans-serif'
                          }}
                        >
                          {t('branches.totalBranches')}
                        </Typography>
                        <Typography 
                          variant="h4" 
                          sx={{ 
                            fontWeight: 700,
                            color: '#4CAF50',
                            fontFamily: '"Poppins", sans-serif'
                          }}
                        >
                          {branches.length}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card sx={{ 
                  backgroundColor: '#252838',
                  border: '1px solid #3a3d52',
                  borderRadius: 3,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Schedule sx={{ fontSize: 40, mr: 2, color: '#4CAF50' }} />
                      <Box>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: '#9ca3af',
                            fontFamily: '"Poppins", sans-serif'
                          }}
                        >
                          {t('branches.activeBranches')}
                        </Typography>
                        <Typography 
                          variant="h4" 
                          sx={{ 
                            fontWeight: 700,
                            color: '#4CAF50',
                            fontFamily: '"Poppins", sans-serif'
                          }}
                        >
                          {branches.filter(b => b.status === 'active').length}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card sx={{ 
                  backgroundColor: '#252838',
                  border: '1px solid #3a3d52',
                  borderRadius: 3,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Phone sx={{ fontSize: 40, mr: 2, color: '#4CAF50' }} />
                      <Box>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: '#9ca3af',
                            fontFamily: '"Poppins", sans-serif'
                          }}
                        >
                          {t('branches.todaysRevenue')}
                        </Typography>
                        <Typography 
                          variant="h4" 
                          sx={{ 
                            fontWeight: 700,
                            color: theme.palette.success.main,
                            fontFamily: '"Poppins", sans-serif'
                          }}
                        >
                          {formatCurrency(branches.reduce((sum, branch) => sum + branch.revenue_today, 0))}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Mobile Card View */}
            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              {branches.map((branch) => (
                <Card key={branch.id} sx={{
                  backgroundColor: '#252838',
                  border: '1px solid #3a3d52',
                  borderRadius: 2,
                  mb: 1.5,
                }}>
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ color: '#e0e0e0', fontWeight: 600, fontFamily: '"Poppins", sans-serif', fontSize: '0.95rem' }}>
                          {branch.name}
                        </Typography>
                        <Typography sx={{ color: '#9ca3af', fontSize: '0.8rem', fontFamily: '"Poppins", sans-serif', mt: 0.25 }}>
                          {branch.address}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
                        <IconButton size="small" onClick={() => setScheduleBranch(branch)} sx={{ color: '#00C896', '&:hover': { color: '#b6ff4d' } }} title={t('schedule.manage', 'Schedule')}>
                          <EventBusy fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleOpenDialog(branch)} sx={{ color: '#9ca3af', '&:hover': { color: '#e0e0e0' } }}>
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDeleteBranch(branch.id)} sx={{ color: '#d32f2f', '&:hover': { color: '#F44336' } }}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                      <Chip
                        label={branch.status}
                        size="small"
                        sx={{
                          backgroundColor: branch.status === 'active' ? '#4CAF50' : '#999999',
                          color: '#1a1d2e',
                          fontFamily: '"Poppins", sans-serif',
                          fontWeight: 500,
                          textTransform: 'capitalize',
                          fontSize: '0.7rem',
                          height: 22,
                        }}
                      />
                      <Typography sx={{ color: '#9ca3af', fontSize: '0.8rem', fontFamily: '"Poppins", sans-serif' }}>
                        {branch.orders_today} {t('branches.todaysOrders').toLowerCase()}
                      </Typography>
                      <Typography sx={{ color: theme.palette.success.main, fontWeight: 600, fontSize: '0.85rem', fontFamily: '"Poppins", sans-serif' }}>
                        {formatCurrency(branch.revenue_today)}
                      </Typography>
                    </Box>
                    {branch.manager && (
                      <Typography sx={{ color: '#666', fontSize: '0.75rem', fontFamily: '"Poppins", sans-serif', mt: 0.75 }}>
                        {t('branches.manager')}: {branch.manager}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>

            {/* Desktop Branches Table */}
            <Paper sx={{
              backgroundColor: '#252838',
              border: '1px solid #3a3d52',
              borderRadius: 3,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
              display: { xs: 'none', md: 'block' },
            }}>
              <TableContainer sx={{
                maxHeight: { xs: 'none', md: '70vh' },
                overflow: 'auto',
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: '#f1f1f1',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#c1c1c1',
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: '#a8a8a8',
                  },
                },
              }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#1e2139' }}>
                      <TableCell sx={{ 
                        color: '#e0e0e0', 
                        fontWeight: 600,
                        fontFamily: '"Poppins", sans-serif',
                        borderBottom: '2px solid #3a3d52'
                      }}>
                        {t('branches.branchName')}
                      </TableCell>
                      <TableCell sx={{
                        color: '#e0e0e0',
                        fontWeight: 600,
                        fontFamily: '"Poppins", sans-serif',
                        borderBottom: '2px solid #3a3d52'
                      }}>
                        {t('branches.address')}
                      </TableCell>
                      <TableCell sx={{
                        color: '#e0e0e0',
                        fontWeight: 600,
                        fontFamily: '"Poppins", sans-serif',
                        borderBottom: '2px solid #3a3d52'
                      }}>
                        {t('branches.manager')}
                      </TableCell>
                      <TableCell sx={{
                        color: '#e0e0e0',
                        fontWeight: 600,
                        fontFamily: '"Poppins", sans-serif',
                        borderBottom: '2px solid #3a3d52'
                      }}>
                        {t('common.status')}
                      </TableCell>
                      <TableCell sx={{
                        color: '#e0e0e0',
                        fontWeight: 600,
                        fontFamily: '"Poppins", sans-serif',
                        borderBottom: '2px solid #3a3d52'
                      }}>
                        {t('branches.todaysOrders')}
                      </TableCell>
                      <TableCell sx={{
                        color: '#e0e0e0',
                        fontWeight: 600,
                        fontFamily: '"Poppins", sans-serif',
                        borderBottom: '2px solid #3a3d52'
                      }}>
                        {t('branches.todaysRevenue')}
                      </TableCell>
                      <TableCell sx={{
                        color: '#e0e0e0',
                        fontWeight: 600,
                        fontFamily: '"Poppins", sans-serif',
                        borderBottom: '2px solid #3a3d52'
                      }}>
                        {t('common.actions')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {branches.map((branch) => (
                      <TableRow 
                        key={branch.id} 
                        sx={{ 
                          '&:hover': { backgroundColor: '#1e2139' },
                          '&:last-child td': { border: 0 }
                        }}
                      >
                        <TableCell sx={{ 
                          color: '#e0e0e0', 
                          fontWeight: 600,
                          fontFamily: '"Poppins", sans-serif'
                        }}>
                          {branch.name}
                        </TableCell>
                        <TableCell sx={{ 
                          color: '#9ca3af',
                          fontFamily: '"Poppins", sans-serif'
                        }}>
                          {branch.address}
                        </TableCell>
                        <TableCell sx={{ 
                          color: '#9ca3af',
                          fontFamily: '"Poppins", sans-serif'
                        }}>
                          {branch.manager}
                        </TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>
                          <Chip
                            label={branch.status}
                            size="small"
                            sx={{
                              backgroundColor: branch.status === 'active' ? '#4CAF50' : '#999999',
                              color: '#1a1d2e',
                              fontFamily: '"Poppins", sans-serif',
                              fontWeight: 500,
                              textTransform: 'capitalize'
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ 
                          color: '#9ca3af',
                          fontFamily: '"Poppins", sans-serif'
                        }}>
                          {branch.orders_today}
                        </TableCell>
                        <TableCell sx={{ 
                          color: theme.palette.success.main,
                          fontWeight: 600,
                          fontFamily: '"Poppins", sans-serif'
                        }}>
                          {formatCurrency(branch.revenue_today)}
                        </TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <IconButton
                              size="small"
                              onClick={() => setScheduleBranch(branch)}
                              title={t('schedule.manage', 'Schedule')}
                              sx={{
                                color: '#00C896',
                                '&:hover': {
                                  color: '#b6ff4d',
                                  backgroundColor: '#1e2139'
                                }
                              }}
                            >
                              <EventBusy />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDialog(branch)}
                              sx={{
                                color: '#9ca3af',
                                '&:hover': {
                                  color: '#e0e0e0',
                                  backgroundColor: '#1e2139'
                                }
                              }}
                            >
                              <Edit />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteBranch(branch.id)}
                              sx={{
                                color: '#d32f2f',
                                '&:hover': {
                                  backgroundColor: 'rgba(244, 67, 54, 0.12)'
                                }
                              }}
                            >
                              <Delete />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </>
        )}

        {/* Add/Edit Branch Dialog */}
        <Dialog 
          open={openDialog} 
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              backgroundColor: '#252838',
              borderRadius: 3,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              minWidth: '500px',
              maxWidth: '600px'
            }
          }}
        >
          <DialogTitle sx={{ 
            color: '#e0e0e0',
            fontFamily: '"Poppins", sans-serif',
            fontWeight: 600,
            borderBottom: '1px solid #3a3d52',
            pb: 2.5,
            pt: 3,
            px: 3
          }}>
            {editingBranch ? t('branches.editBranch') : t('branches.addBranch')}
          </DialogTitle>
          <DialogContent sx={{ 
            p: { xs: 1.5, md: 3 }, 
            pt: 4,
            '&.MuiDialogContent-root': {
              paddingTop: '24px'
            }
          }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('branches.branchName')}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                    style: { 
                      fontSize: '0.95rem',
                      backgroundColor: '#252838',
                      paddingLeft: '4px',
                      paddingRight: '4px'
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '0.95rem'
                    },
                    '& .MuiInputLabel-outlined': {
                      transform: 'translate(14px, 14px) scale(1)',
                      '&.MuiInputLabel-shrink': {
                        transform: 'translate(14px, -9px) scale(0.75)',
                        backgroundColor: '#252838',
                        paddingLeft: '4px',
                        paddingRight: '4px'
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('branches.manager')}
                  value={formData.manager}
                  onChange={(e) => setFormData({ ...formData, manager: e.target.value })}
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                    style: { 
                      fontSize: '0.95rem',
                      backgroundColor: '#252838',
                      paddingLeft: '4px',
                      paddingRight: '4px'
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '0.95rem'
                    },
                    '& .MuiInputLabel-outlined': {
                      transform: 'translate(14px, 14px) scale(1)',
                      '&.MuiInputLabel-shrink': {
                        transform: 'translate(14px, -9px) scale(0.75)',
                        backgroundColor: '#252838',
                        paddingLeft: '4px',
                        paddingRight: '4px'
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('branches.address')}
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                    style: { 
                      fontSize: '0.95rem',
                      backgroundColor: '#252838',
                      paddingLeft: '4px',
                      paddingRight: '4px'
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '0.95rem'
                    },
                    '& .MuiInputLabel-outlined': {
                      transform: 'translate(14px, 14px) scale(1)',
                      '&.MuiInputLabel-shrink': {
                        transform: 'translate(14px, -9px) scale(0.75)',
                        backgroundColor: '#252838',
                        paddingLeft: '4px',
                        paddingRight: '4px'
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('common.phone')}
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                    style: { 
                      fontSize: '0.95rem',
                      backgroundColor: '#252838',
                      paddingLeft: '4px',
                      paddingRight: '4px'
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '0.95rem'
                    },
                    '& .MuiInputLabel-outlined': {
                      transform: 'translate(14px, 14px) scale(1)',
                      '&.MuiInputLabel-shrink': {
                        transform: 'translate(14px, -9px) scale(0.75)',
                        backgroundColor: '#252838',
                        paddingLeft: '4px',
                        paddingRight: '4px'
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('common.email')}
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  variant="outlined"
                  type="email"
                  InputLabelProps={{
                    shrink: true,
                    style: { 
                      fontSize: '0.95rem',
                      backgroundColor: '#252838',
                      paddingLeft: '4px',
                      paddingRight: '4px'
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '0.95rem'
                    },
                    '& .MuiInputLabel-outlined': {
                      transform: 'translate(14px, 14px) scale(1)',
                      '&.MuiInputLabel-shrink': {
                        transform: 'translate(14px, -9px) scale(0.75)',
                        backgroundColor: '#252838',
                        paddingLeft: '4px',
                        paddingRight: '4px'
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('branches.openingHours')}
                  value={formData.opening_hours}
                  onChange={(e) => setFormData({ ...formData, opening_hours: e.target.value })}
                  placeholder="e.g., 10:00 - 22:00"
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                    style: { 
                      fontSize: '0.95rem',
                      backgroundColor: '#252838',
                      paddingLeft: '4px',
                      paddingRight: '4px'
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '0.95rem'
                    },
                    '& .MuiInputLabel-outlined': {
                      transform: 'translate(14px, 14px) scale(1)',
                      '&.MuiInputLabel-shrink': {
                        transform: 'translate(14px, -9px) scale(0.75)',
                        backgroundColor: '#252838',
                        paddingLeft: '4px',
                        paddingRight: '4px'
                      }
                    }
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: { xs: 1.5, md: 3 }, borderTop: '1px solid #3a3d52' }}>
            <Button 
              onClick={handleCloseDialog}
              sx={{ 
                color: '#9ca3af',
                fontFamily: '"Poppins", sans-serif'
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleSaveBranch}
              variant="contained"
              sx={{
                backgroundColor: '#00C896',
                color: '#1a1d2e',
                '&:hover': { backgroundColor: '#7bc912' },
                fontFamily: '"Poppins", sans-serif',
                fontWeight: 600
              }}
            >
              {editingBranch ? t('branches.updateBranch') : t('branches.addBranch')}
            </Button>
          </DialogActions>
        </Dialog>

        {scheduleBranch && (
          <BranchScheduleDialog
            open={!!scheduleBranch}
            branchId={scheduleBranch.id as string}
            branchName={scheduleBranch.name}
            onClose={() => setScheduleBranch(null)}
          />
        )}
      </Box>
    </AdminLayout>
  );
};

export default BranchesPage;
